/* eslint-disable no-underscore-dangle */

// eslint-disable-next-line import/no-named-default
import { default as uniqid } from 'uniqid'; // Webpack bundles this incorrectly unless this wacky import syntax is used
import { addAuthStrategy, setCacheStrategy } from '@nab/x-auth';
import { registerLoader, XV1AppLoader, XV2AppLoader } from '@nab/x-app-loaders';
import type { XV1App, XV2App } from '@nab/x-types';
import { createServices, setShellServices, registerServicesProvider, buildUser, registerActionsProvider, setShellActions } from '@nab/x-registries';
import { RuntimeInitConfig, Runtime } from './types';
import { reset, setAuthConfig, setSessionId } from './core';
import { extendAppLoader } from './utils/extendAppLoader';
import { getRegisteredApp, registerInstance } from './registries/instanceRegistry';
import { createActionsForApp } from './actions/createActionsForApp';

/**
 * Configures x-runtime
 */
export async function initRuntime(initConfig: RuntimeInitConfig): Promise<Runtime> {
    reset();

    // Dirty hack to provide backward compatibility with older mini-apps which depend on the legacy app loader
    window.__nabxAppLoader__ = window.__nabxAppLoader__ || {
        getConfig: (id: string) => {
            const reg = getRegisteredApp(id);
            return (
                (reg && { manifest: (<XV2App>reg.appMetaData).assetManifestUrl }) ||
                (window.__nabxAppLoader__.loadedApp && window.__nabxAppLoader__.loadedApp[id] && window.__nabxAppLoader__.loadedApp[id])
            );
        }
    };

    if (!initConfig.authConfig || !initConfig.authConfig.environment || !initConfig.authConfig.context) {
        throw new Error('Invalid authConfig. authConfig must be a plain object which includes environment and context');
    }

    if (!initConfig.userConfig) {
        throw new Error('userConfig must be provided to initialise the x-runtime');
    }

    // Create a unique session id. This will be provided to all mini-apps to help with logging correlation
    const sessionId = initConfig.sessionId || uniqid();
    setSessionId(sessionId);

    // Register default auth config. This config will be inherited by all apps in the shell with their specific overrides being applied over the top
    const authConfig = {
        tokenType: 'Bearer',
        tokenSource: 'DAF',
        requestTokenOnLoad: true,
        ...initConfig.authConfig
    };
    setAuthConfig(authConfig);

    // Register token caching strategy with x-auth
    setCacheStrategy(initConfig.authTokenCacheStrategy);

    // Register auth strategies with x-auth
    if (initConfig.authStrategies) {
        for (const s of initConfig.authStrategies) {
            addAuthStrategy(s);
        }
    }

    // Register any overrides to actions providers
    if (initConfig.actionsProvider) {
        registerActionsProvider(initConfig.actionsProvider);
    }

    // Register services provider to override the default services provider
    if (initConfig.servicesProvider) {
        registerServicesProvider(initConfig.servicesProvider);
    }

    // Augment XV2 app loader to ensure event subscriptions are unbound when an app is closed
    registerLoader(
        {
            constructor: extendAppLoader<XV2App>(XV2AppLoader),
            supports: 'XV2'
        },
        true
    );

    // Augment XV1 app loader to ensure event subscriptions are unbound when an app is closed
    registerLoader(
        {
            constructor: extendAppLoader<XV1App>(XV1AppLoader),
            supports: 'XV1'
        },
        true
    );

    // Register an custom app loaders
    if (initConfig.appLoaders) {
        for (const l of [...initConfig.appLoaders].reverse()) {
            registerLoader({
                ...l,
                constructor: extendAppLoader(l.constructor)
            });
        }
    }

    /**
     * Register an app instance for the shell itself
     *
     * This allows us to create an AppActions set for the shell so it too can perform event dispatch/listen, authentication etc...
     */
    const instanceId = uniqid();
    const rootInstance = {
        appMetaData: {
            /**
             * There's a conflict here. Shell app id is always 'root' for both x-runtime and x-spa based shells however
             * the flag manager expects the shell's app id to be 'SHELL'
             */
            id: 'SHELL',
            name: 'x-runtime application',
            appType: 'XRUNTIMEV1' // This is irrelevant for the shell itself as it is not loaded using an App Loader
        },
        authConfig: {
            ...authConfig,
            isSeed: true
        },
        appConfig: {
            sessionId,
            instanceId
        },
        hostElement: document.body
    };
    registerInstance(instanceId, rootInstance);

    const shellServices = createServices(initConfig.servicesConfig);
    setShellServices(shellServices);

    // Build User based on user config provided
    const user = buildUser(
        initConfig.userConfig,
        {
            appMetaData: rootInstance.appMetaData,
            appConfig: rootInstance.appConfig
        },
        shellServices.analyticsMgr,
        shellServices.dataSecurityMgr,
        shellServices.flagMgr
    );

    if (initConfig.servicesConfig?.flags?.strategy && user.tailoring?.setFlagStrategy) {
        await user.tailoring.setFlagStrategy(initConfig.servicesConfig.flags.strategy);
    }

    // Return the AppActions set for the shell itself
    const shellActions = createActionsForApp(rootInstance.appMetaData, rootInstance.appConfig.instanceId);
    setShellActions(shellActions);

    return {
        shellActions,
        shellServices,
        user
    };
}
